import { type ComponentProps, type RefObject } from 'react'

import { mapLogger, MarkerType, TKeywordMarker } from '@/entities/map'
import { NoSearchResult } from '@/entities/room'
import { useDebounce } from '@/shared/lib'
import { Spinner, Badge, Highlight, Icon } from '@/shared/ui'

import { useKeywordQuery } from '../../api/get-keyword'
import { getLocationName } from '../../lib/location'
import { Item, List } from '../popup/common'
import { Popup } from '../popup/popup'

const MIN_INPUT_LENGTH = 2

type Props = ComponentProps<typeof Popup> & {
  ref: RefObject<HTMLDivElement | null>
  value: string
  onKeywordClick: (location: TKeywordMarker) => void
}

export const AutoCompletePopup = ({
  value,
  onKeywordClick,
  ref,
  ...restProps
}: Props) => {
  const debouncedValue = useDebounce<string>(value, 200)

  const {
    loading,
    regions,
    subways,
    landmarks,
    hasRegions,
    hasSubways,
    hasLandmarks,
    hasNoMarker,
  } = useKeywordQuery({
    keyword: debouncedValue,
    enabled:
      value.length >= MIN_INPUT_LENGTH &&
      debouncedValue.length >= MIN_INPUT_LENGTH,
  })

  const createLogData = ({
    location,
    type,
    index,
  }: {
    location: TKeywordMarker
    type: MarkerType
    index: number
  }) => {
    const dataCount = (() => {
      switch (type) {
        case MarkerType.REGION:
          return regions.length
        case MarkerType.SUBWAY:
          return subways.length
        case MarkerType.LANDMARK:
          return landmarks.length
      }
    })()

    return {
      keyword: value,
      type,
      dataCount,
      markerName: location.name,
      selectedIndex: index,
    }
  }

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault()
  }

  const handleItemClick = ({
    location,
    type,
    index,
  }: {
    location: TKeywordMarker
    type: MarkerType
    index: number
  }) => {
    return async () => {
      onKeywordClick(location)

      const log = createLogData({
        location,
        type,
        index,
      })

      await mapLogger.clickKeywords(log)
    }
  }

  return (
    <Popup
      className="flex h-[564px] w-[1082px] overflow-hidden rounded-xl"
      onOpenAutoFocus={handleOpenAutoFocus}
      autoFocus={true}
      ref={ref}
      {...restProps}
    >
      {(() => {
        if (loading) {
          return (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4">
              <Spinner />
            </div>
          )
        }

        if (hasNoMarker) {
          return (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4">
              <NoSearchResult />
              <h3 className="typo-headline2 font-bold text-black">
                검색 결과가 없습니다
              </h3>
              <p className="typo-body1 text-center text-gray-900">
                지역, 지하철, 장소 이름을
                <br />두 글자 이상 입력해 주세요.
              </p>
            </div>
          )
        }

        return (
          <>
            {hasRegions && (
              <List title="지역" className="w-[360px]">
                {(() => {
                  return regions.map((region, index) => (
                    <Item
                      key={`${region.name}-${region.lat}-${region.lng}`}
                      onClick={handleItemClick({
                        location: region,
                        type: MarkerType.REGION,
                        index,
                      })}
                    >
                      <Highlight query={value} className="inline">
                        {getLocationName(region)}
                      </Highlight>
                    </Item>
                  ))
                })()}
              </List>
            )}
            {hasSubways && (
              <List title="지하철" className="w-[360px]">
                {(() => {
                  return subways.map((subway, index) => (
                    <Item
                      key={`${subway.name}-${subway.lat}-${subway.lng}`}
                      onClick={handleItemClick({
                        location: subway,
                        type: MarkerType.SUBWAY,
                        index,
                      })}
                    >
                      <Highlight query={value} className="inline">
                        {subway.name}
                      </Highlight>
                      <ul className="inline-flex max-w-[110px] flex-wrap items-center justify-end gap-1">
                        {subway.subwayLines.map(line => (
                          <li key={line.name} className="flex">
                            <Badge
                              key={line.name}
                              variant="solid"
                              color={line.color}
                            >
                              {line.displayName}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    </Item>
                  ))
                })()}
              </List>
            )}
            {hasLandmarks && (
              <List title="랜드마크" className="w-[360px]">
                {(() => {
                  return landmarks.map((landmark, index) => (
                    <Item
                      key={`${landmark.name}-${landmark.lat}-${landmark.lng}`}
                      onClick={handleItemClick({
                        location: landmark,
                        type: MarkerType.LANDMARK,
                        index,
                      })}
                    >
                      <Highlight query={value} className="inline">
                        {landmark.name}
                      </Highlight>
                      <Icon name="flag" size={18} className="text-blue-500" />
                    </Item>
                  ))
                })()}
              </List>
            )}
          </>
        )
      })()}
    </Popup>
  )
}
